import { useState, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Download, Upload, Trash2, AlertTriangle, ClipboardList,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Button, EmptyState } from "@/components/ui";
import {
  uploadProjectDeliverable, uploadMasterTemplate,
  getDeliverableSignedUrl, deleteDeliverableUpload,
} from "@/features/deliverables/deliverablesUpload";
import { format, parseISO, formatDistanceToNow } from "date-fns";

// =============================================================================
// PackAcceptanceDocsSection
//
// Renders the Acceptance category deliverable lines (Red Line Markups,
// As Built SID, Sweep Tests, MW Test Report, Delivery Dockets) inline within
// a HOP pack detail page. Same R2 backend as ProjectDeliverablesScreen,
// just scoped to a single category and embedded in the pack context.
//
// Why here: per James 2026-04-25, acceptance documents belong WITH the HOP
// pack (the acceptance package), not on a separate Deliverables surface.
// Photos + Documents are the two halves of acceptance — both live here.
// =============================================================================

interface Props {
  projectId: string;
  canManage: boolean;
}

interface Line {
  line_id:                     string;
  category_id:                 string;
  carrier_id:                  string | null;
  code:                        string;
  display_name:                string;
  description:                 string | null;
  has_master:                  boolean;
  master_storage_path:         string | null;
  master_version_label:        string | null;
  master_uploaded_at:          string | null;
  master_file_size_bytes:      number | null;
  master_file_name:            string | null;
  sort_order:                  number;
}

interface Upload {
  upload_id:               string;
  line_id:                 string;
  storage_path:            string;
  file_name:               string;
  file_size_bytes:         number | null;
  mime_type:               string | null;
  version_label:           string | null;
  status:                  "draft" | "submitted" | "accepted" | "archived";
  uploaded_at:             string;
  uploaded_by_employee_id: string | null;
  is_archived:             boolean;
}

const STATUS_TONE: Record<Upload["status"], "open" | "pending" | "approved" | "default"> = {
  draft:     "default",
  submitted: "pending",
  accepted:  "approved",
  archived:  "default",
};

// -----------------------------------------------------------------------------
export default function PackAcceptanceDocsSection({ projectId, canManage }: Props) {
  const qc = useQueryClient();

  // Project carrier — used to filter carrier-specific lines
  const { data: project } = useQuery({
    queryKey: ["pack_acc_docs_project", projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from("projects")
        .select("project_id, carrier_id")
        .eq("project_id", projectId)
        .maybeSingle();
      return data as { project_id: string; carrier_id: string | null } | null;
    },
  });

  // Acceptance lines (filtered by carrier or generic)
  const { data: lines = [] } = useQuery({
    queryKey: ["pack_acc_docs_lines", project?.carrier_id ?? null],
    enabled: project !== undefined,
    queryFn: async () => {
      // First find the acceptance category id
      const { data: cat } = await supabase
        .from("document_library_categories")
        .select("category_id")
        .eq("code", "acceptance")
        .maybeSingle();
      if (!cat) return [];

      let q = supabase
        .from("document_library_lines")
        .select(`line_id, category_id, carrier_id, code, display_name,
                 description, has_master, master_storage_path,
                 master_version_label, master_uploaded_at,
                 master_file_size_bytes, master_file_name, sort_order`)
        .eq("category_id", cat.category_id)
        .eq("is_active", true)
        .order("sort_order");
      if (project?.carrier_id) {
        q = q.or(`carrier_id.is.null,carrier_id.eq.${project.carrier_id}`);
      } else {
        q = q.is("carrier_id", null);
      }
      const { data } = await q;
      return (data ?? []) as Line[];
    },
  });

  // Project's uploads against these lines
  const { data: uploads = [], refetch: refetchUploads } = useQuery({
    queryKey: ["pack_acc_docs_uploads", projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from("document_library_uploads")
        .select(`upload_id, line_id, storage_path, file_name,
                 file_size_bytes, mime_type, version_label, status,
                 uploaded_at, uploaded_by_employee_id, is_archived`)
        .eq("project_id", projectId)
        .eq("is_archived", false)
        .order("uploaded_at", { ascending: false });
      return (data ?? []) as Upload[];
    },
  });

  const uploadsByLine = useMemo(() => {
    const m = new Map<string, Upload[]>();
    for (const u of uploads) {
      const arr = m.get(u.line_id) ?? [];
      arr.push(u);
      m.set(u.line_id, arr);
    }
    return m;
  }, [uploads]);

  if (lines.length === 0) {
    return (
      <Card>
        <div className="p-4">
          <EmptyState
            title="No acceptance document types configured"
            description="Acceptance lines (Red Line Markups, As Built SID, etc.) are seeded via SQL — check migration 047."
            icon={ClipboardList}
          />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {lines.map(line => (
        <DocLineCard
          key={line.line_id}
          line={line}
          uploads={uploadsByLine.get(line.line_id) ?? []}
          projectId={projectId}
          canManage={canManage}
          onChanged={() => {
            refetchUploads();
            qc.invalidateQueries({ queryKey: ["pack_acc_docs_lines"] });
          }}
        />
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
function DocLineCard({
  line, uploads, projectId, canManage, onChanged,
}: {
  line:      Line;
  uploads:   Upload[];
  projectId: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const masterInputRef = useRef<HTMLInputElement>(null);

  const [busyUpload, setBusyUpload] = useState<string | null>(null);
  const [busyMaster, setBusyMaster] = useState<string | null>(null);
  const [error,      setError]      = useState<string | null>(null);

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    for (const file of Array.from(files)) {
      try {
        setBusyUpload(`uploading ${file.name}…`);
        await uploadProjectDeliverable({
          projectId, lineId: line.line_id, file,
        });
      } catch (e: any) {
        setError(e?.message ?? "Upload failed");
        setBusyUpload(null);
        return;
      }
    }
    setBusyUpload(null);
    onChanged();
  }

  async function handleMasterUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    try {
      setBusyMaster(`uploading…`);
      await uploadMasterTemplate({ lineId: line.line_id, file: files[0] });
    } catch (e: any) {
      setError(e?.message ?? "Master upload failed");
      setBusyMaster(null);
      return;
    }
    setBusyMaster(null);
    onChanged();
  }

  async function handleDownload(storagePath: string, filename: string) {
    const url = await getDeliverableSignedUrl(storagePath, `attachment; filename="${filename}"`);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleDeleteUpload(u: Upload) {
    if (!window.confirm(`Delete "${u.file_name}"?`)) return;
    try {
      await deleteDeliverableUpload(u.upload_id);
    } catch (e: any) {
      window.alert(`Delete failed: ${e?.message ?? "unknown"}`);
      return;
    }
    onChanged();
  }

  return (
    <Card>
      <div className="p-3 md:p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[14px] font-semibold text-text">{line.display_name}</h3>
              <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">{line.code}</span>
              {line.carrier_id && (
                <span className="text-[9px] font-mono uppercase tracking-wider2 text-accent px-1.5 py-0.5 rounded bg-accent/10 border border-accent/30">
                  carrier
                </span>
              )}
            </div>
            {line.description && (
              <p className="text-[11px] text-text-muted mt-0.5 leading-snug">{line.description}</p>
            )}
          </div>
          <div className="text-[10px] font-mono text-text-muted flex-shrink-0">
            {uploads.length} upload{uploads.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* Master template (if line has one) */}
        {line.has_master && (
          <div className="mt-2 mb-2 p-2.5 rounded bg-ink border border-line">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={12} className="text-accent flex-shrink-0"/>
                <div className="min-w-0">
                  <div className="text-[11px] text-text font-medium">Master template</div>
                  <div className="text-[10px] font-mono text-text-muted truncate">
                    {line.master_storage_path
                      ? <>
                          {line.master_file_name ?? "template"}
                          {line.master_version_label && <> · <span className="text-accent">{line.master_version_label}</span></>}
                        </>
                      : <span className="italic text-text-dim">No master uploaded yet</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {line.master_storage_path && (
                  <Button
                    variant="secondary" size="sm" icon={Download}
                    onClick={() => handleDownload(line.master_storage_path!, line.master_file_name ?? "master")}
                  >
                    Download
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button
                      variant="ghost" size="sm" icon={Upload}
                      onClick={() => masterInputRef.current?.click()}
                      disabled={busyMaster !== null}
                    >
                      {busyMaster ?? (line.master_storage_path ? "Replace" : "Upload master")}
                    </Button>
                    <input
                      ref={masterInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => { handleMasterUpload(e.target.files); e.target.value = ""; }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Project uploads */}
        <div className="mt-2">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">
              This pack's uploads
            </div>
            <Button
              variant="primary" size="sm" icon={Upload}
              onClick={() => uploadInputRef.current?.click()}
              disabled={busyUpload !== null}
            >
              {busyUpload ?? "Upload new"}
            </Button>
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
            />
          </div>

          {uploads.length === 0 ? (
            <div className="text-[11px] text-text-muted italic">
              No uploads yet — add the filled / signed version when ready.
            </div>
          ) : (
            <div className="space-y-1">
              {uploads.map(u => (
                <UploadRow
                  key={u.upload_id}
                  upload={u}
                  canDelete={canManage}
                  onDownload={() => handleDownload(u.storage_path, u.file_name)}
                  onDelete={() => handleDeleteUpload(u)}
                />
              ))}
            </div>
          )}

          {error && (
            <div className="mt-2 text-[11px] text-danger flex items-start gap-1.5">
              <AlertTriangle size={11} className="flex-shrink-0 mt-0.5"/>
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
function UploadRow({
  upload, canDelete, onDownload, onDelete,
}: {
  upload:     Upload;
  canDelete:  boolean;
  onDownload: () => void;
  onDelete:   () => void;
}) {
  function fmtBytes(n: number | null): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }
  return (
    <div className="flex items-center gap-2 p-2 rounded bg-ink border border-line">
      <FileText size={12} className="text-text-muted flex-shrink-0"/>
      <button
        onClick={onDownload}
        type="button"
        className="flex-1 min-w-0 text-left text-[11px] text-text hover:text-accent truncate"
      >
        {upload.file_name}
      </button>
      {upload.version_label && (
        <span className="text-[9px] font-mono text-accent bg-accent/10 border border-accent/30 px-1 py-0.5 rounded">
          {upload.version_label}
        </span>
      )}
      <Pill label={upload.status} tone={STATUS_TONE[upload.status]} small />
      <span className="text-[9px] text-text-muted font-mono flex-shrink-0">
        {fmtBytes(upload.file_size_bytes)}
      </span>
      <span className="text-[9px] text-text-dim font-mono flex-shrink-0 hidden sm:inline">
        {format(parseISO(upload.uploaded_at), "dd MMM")}
      </span>
      {canDelete && (
        <button
          onClick={onDelete}
          type="button"
          className="text-text-muted hover:text-danger flex-shrink-0 p-0.5"
          title="Delete"
        >
          <Trash2 size={11}/>
        </button>
      )}
    </div>
  );
}
